(ns ^:dev/always bp-clients.tv-app.nav.events
  "Namespace for all navigation events"
  (:require
   [bp-clients.tv-app.analytics.events :as analytics-events]
   [bp-clients.tv-app.api.events :as api]
   [bp-clients.tv-app.api.graphql.castle :as castle]
   [bp-clients.tv-app.api.graphql.menu :as menu]
   [bp-clients.tv-app.api.graphql.page :as page]
   [bp-clients.tv-app.api.graphql.search :as search]
   [bp-clients.tv-app.api.graphql.title :as playlist-queries]
   [bp-clients.tv-app.auth.events :as auth-events]
   [bp-clients.tv-app.common.events :as common-events]
   [bp-clients.tv-app.router :as router :refer [navigate-to! back-navigation! replace-state]]
   [bp-clients.tv-app.utils.keycodes :as keycodes]
   [clojure.set :refer [union]]
   [clojure.string :as s]
   [goog.functions :refer [debounce]]
   [re-frame.core
    :as rf
    :refer [inject-cofx path reg-event-db reg-event-fx reg-fx]]))

(def ^:private nav-path ::nav)

(def ^:private nav-interceptors
  "Checks spec and selects nav path interceptor"
  [(path nav-path)])

(defn- navigate-back-ev
  "Given a path. Navigates to it."
  [{:keys [db]}]
  {:db (assoc db :back-nav? true)
   ::back-navigation []})

(def unauthenticated-routes
  #{::router/home ::router/login ::router/register})

(defn- navigate-to-ev
  "Given a path. Navigates to it."
  [{:keys [db]} [_event path-name opts]]
  {:db (update db :nav dissoc :active-playlist)
   ::navigate-to {:path-name path-name :opts opts}})

(defn- route-changed-ev
  "Given the navigation state. Updates its active-* states."
  [{nav :db token ::auth-events/auth-token} [_event-name {:keys [data path-params redirect] :as _route-params}]]
  (let [logged? (seq token)
        {:keys [name]} data
        {:keys [page-id playlist-id]} path-params
        nav (dissoc nav :active-playlist)
        state (case name
                ::router/upgrade
                {:db nav
                 :dispatch [::analytics-events/screen-requested {:view "upgrade"}]}

                ::router/search
                {:db       (assoc nav :active-page :search)
                 :dispatch-n [[::set-active-menu :search]
                              [::analytics-events/screen-requested {:view "search"}]]}

                ::router/home
                {:db (assoc nav :active-page :home)}

                (::router/page-index ::router/page)
                {:db       (assoc nav :active-page :page-list)
                 :dispatch-n [[::set-active-menu (-> page-id (or :home) keyword)]
                              [::analytics-events/screen-requested {:view (or page-id :home)}]]}

                ::router/logout
                {:db (assoc nav :active-page :logout)
                 :dispatch [::analytics-events/screen-requested {:view :logout}]}

                ::router/login
                {:db (assoc nav :active-page :login)
                 :dispatch [::analytics-events/screen-requested {:view :login}]}

                ::router/register
                {:db (assoc nav :active-page :register)
                 :dispatch [::analytics-events/screen-requested {:view :register}]}

                ::router/playlist-page
                {:db       (assoc nav :active-page :playlist)
                 :dispatch-n [[::set-active-playlist playlist-id]
                              [::analytics-events/screen-requested {:view :playlist-page
                                                                    :item-id playlist-id}]]}

                ;; not found case
                {:db nav})]

    (cond
      ;; only used at login currently
      redirect
      (replace-state redirect)

      (and (= nil name) logged?)
      (replace-state ::router/page-index)

      (and (contains? unauthenticated-routes name) logged?)
      (replace-state ::router/page-index)

      (not (or (contains? unauthenticated-routes name) logged?))
      (replace-state ::router/home))

    state))

(def ^:private one-minute (* 60 1000))
(def ^:private minimum-page-refresh
  "The minimum time to keep pages cache before trying to refresh locally. In milliseconds."
  (* one-minute 10))

(defn- set-active-menu-ev
  "Given the navigation state and an active-nav. Updates the navigation's active-nav."
  [{:keys [db]} [_event-name active-nav]]
  (let [last-update     (get-in db [:last-refresh active-nav] js/-Infinity)
        now             (.now js/Date)
        should-refresh? (cond
                          (= active-nav :search)
                          (-> db :back-nav? not)

                          :else
                          (>= (- now last-update) minimum-page-refresh))
        fetch-page-ev   [::load-menu active-nav]]
    (cond-> {:db (dissoc db :back-nav?)
             :fx []}
      true                 (assoc-in [:db :active-menu] active-nav)
      should-refresh?      (assoc-in [:db :last-refresh active-nav] now)
      should-refresh?      (update :fx #(conj % [:dispatch fetch-page-ev]))
      (= :home active-nav) (update :fx #(conj % [:dispatch [::keep-watching]])))))

(defn- set-active-playlist-ev
  "Given the navigation state and an active-nav. Updates the navigation's active-nav."
  [{:keys [db]} [_event-name active-playlist]]
  {:db (assoc db :active-playlist active-playlist)
   :dispatch-n [[::fetch-menu]
                [::fetch-playlist active-playlist]]})

(defn- load-menu-ev
  [{:keys [db]} [_event-name menu]]
  (cond

    (or (= :home-bp menu)
        (= :home menu))
    {:db         db
     :dispatch-n [[::fetch-page menu]
                  [::fetch-menu]
                  [::set-menu false]
                  [::keep-watching]]}

    (= :search menu)
    {:db db
     :dispatch-n [[::fetch-search]
                  [::fetch-menu]
                  [::set-menu false]]}

    menu
    {:db         db
     :dispatch-n [[::fetch-page menu]
                  [::fetch-menu]
                  [::set-menu false]]}
    :else
    {:db db}))

(defn- set-active-page-ev
  "Given the navigation state and an active-page. Updates the navigation's active-page."
  [nav [_ active-page]]
  (assoc nav :active-page active-page))

(defn- set-active-item-ev
  "Given the navigation state and an active-page. Updates the navigation's active-page."
  [{:keys [active-menu active-page] :as nav} [_ raw-playlist]]
  (assoc-in nav [:data active-page active-menu :active-item] raw-playlist))

(defn- set-banner-transition-in-progress-ev
  [{:keys [active-menu active-page] :as nav} [_ in-progress?]]
  (assoc-in nav [:data active-page active-menu :banner-transition-in-progress?] in-progress?))

(defn- keep-watching
  "Fetches the session code the user must enter to login."
  [{:keys [db]} [_event-name]]
  {:db         db
   :dispatch [::api/query-graphql
              (merge {:instance-id :auth
                      :on-success  ::keep-watching-success
                      :on-error    [::keep-watching-error]}
                     (castle/keep-watching-query))]})

(defn- keep-watching-error
  "Fetches the session code the user must enter to login."
  ;; @todo
  [{:keys [db]} [_event-name _error]]
  {:db         db})

(defn- keep-watching-success
  "Success event handler for the ::fetch-login-token"
  [{:keys [active-page active-menu] :as db} [_event-name {:keys [decoder data]}]]
  (assoc-in db [:data active-page active-menu :keep-watching] (decoder data)))

(defn fetch-page
  "Fetches the session code the user must enter to login."
  [{:keys [db]} [_event-name page-slug]]
  {:db       (dissoc db :error :paywall :nav-back?)
   :dispatch [::api/query-graphql
              (merge {:instance-id :auth
                      :on-success  ::fetch-page-success
                      :on-error    [::fetch-page-error page-slug]}
                     (page/list-page-query page-slug))]})

(defn- fetch-page-error
  "Stores the error and the event to retry if its connection error."
  [{:keys [db]} [_event-name page-slug {:keys [error-type]}]]
  {:db       db
   :dispatch [::common-events/handle-error {:type     (cond
                                                        (:network-offline? db)   :connection
                                                        (-> error-type nil? not) error-type
                                                        :else                    :unexpected)
                                            :retry-ev [::fetch-page page-slug]}]})

(defn- fetch-page-success
  "Success event handler for the ::fetch-page"
  [{:keys [db]} [_event-name {:keys [decoder data]}]]
  (let [{:keys [active-menu active-page]} db
        decoded        (decoder data)
        sanitized-data (mapv #(assoc % :id (or (:id %) (:title %))) (:components decoded))
        db             (assoc-in db
                                 [:data active-page active-menu :page]
                                 {:components sanitized-data
                                  :cache (.now js/Date)
                                  :keep-watching-position (:keep_watching_position decoded)})]
    {:db       db
     :dispatch [::set-active-item (-> decoded :components first :items first)]}))

(defn fetch-search
  "Fetches search results."
  [{:keys [db]} [_event-name search-terms]]
  (let [{:keys [active-menu active-page]} db
        db (assoc-in db [:data active-page active-menu :search :components] nil)]
    {:db       (-> db
                   (dissoc :error :paywall :nav-back?)
                   (assoc-in [:data active-page active-menu :search :search-term] search-terms))
     :dispatch [::api/query-graphql
                (merge {:instance-id :auth
                        :on-success  ::fetch-search-success
                        :on-error    [::fetch-search-error]}
                       (search/search-query {:search-terms search-terms}))]}))

(defn set-search-term
  [{:keys [db]} [_event-name search-terms]]
  (let [{:keys [active-menu active-page]} db]
    {:db       (assoc-in db [:data active-page active-menu :search :search-term] search-terms)}))

(defn- fetch-search-error
  ;; @todo
  [{:keys [db]} [_event-name _error]]
  {:db         db})

(defn- fetch-search-success
  [{:keys [db]} [_event-name {:keys [decoder data]}]]
  (let [{:keys [active-menu active-page]} db
        decoded                           (decoder data)
        sanitized-data (mapv #(assoc % :id (or (:id %) (:title %))) (:components decoded))]
    {:db (assoc-in db [:data active-page active-menu :search :components] sanitized-data)}))

(defn- fetch-menu
  "Fetches main menu items"
  [{:keys [db]} [_event-name]]
  {:db       (dissoc db :error :paywall :nav-back?)
   :dispatch [::api/query-graphql
              (merge {:instance-id :auth
                      :on-success  ::fetch-menu-success
                      :on-error    [::fetch-menu-error]}
                     (menu/main-menu-query))]})

(defn- fetch-menu-error
  "Fetch menu error"
  ;; @todo
  [{:keys [db]} [_event-name _error]]
  {:db         db})

(defn- fetch-menu-success
  "Success event handler for the ::fetch-menu"
  [{:keys [db]} [_event-name {:keys [decoder data]}]]
  (let [decoded (decoder data)]
    {:db       (assoc db :menu-items (:items decoded))
     :dispatch [::set-active-item (-> decoded :components first :item first)]}))

(defn- fetch-playlist
  "Fetches a playlist details"
  [{:keys [db]} [_event-name playlist-slug]]
  {:db       (dissoc db :error :paywall :nav-back?)
   :dispatch [::api/query-graphql
              (merge {:instance-id :auth
                      :on-success  ::fetch-playlist-success
                      :on-error    [::fetch-playlist-error playlist-slug]}
                     (playlist-queries/by-slug playlist-slug))]})

(defn- fetch-playlist-error
  "Stores the error and the event to retry if its connection error."
  [{:keys [db]} [_event-name playlist-slug {:keys [error-type]}]]
  {:db       db
   :dispatch [::common-events/handle-error {:type     (cond
                                                        (:network-offline? db)   :connection
                                                        (-> error-type nil? not) error-type
                                                        :else                    :unexpected)
                                            :retry-ev [::fetch-playlist playlist-slug]}]})

(defn- fetch-playlist-success
  "Success event handler for the ::fetch-playlist"
  [{:keys [db]} [_event-name {:keys [decoder data]}]]
  (let [{:keys [active-playlist active-page]} db
        decoded (decoder data)
        db      (assoc-in db [:data active-page active-playlist] decoded)]
    (case (:__typename decoded)
      "tv_app_error_page"
      {:db db
       ;; @todo trust on error type returned by api
       :dispatch [::fetch-playlist-error {:error-type :not-found}]}

      "tv_app_title_detail_page_content"
      {:db db}

      "tv_app_paywall"
      {:db (assoc db :paywall decoded)}

      {:db db
       :dispatch [::fetch-playlist-error {:error-type :unexpected}]})))

(defn- set-menu-open-state-ev
  [nav [_event-name open?]]
  (assoc nav :menu-open? open?))

(defn- toggle-menu-ev
  [nav [_event-name open?]]
  (if open?
    (.focus js/SpatialNavigation "menu")
    (.focus js/SpatialNavigation "content"))
  nav)

(defn- menu-item-with-index
  [index menu-item]
  (-> menu-item
      (assoc :index index)
      (assoc :id (-> menu-item
                     :text
                     s/lower-case
                     (s/replace " " "-")
                     keyword))))

(defn- set-menu-items-ev
  [nav [_event-name items]]
  (let [items-with-ids (map-indexed menu-item-with-index items)
        ids-list       (mapv :id items-with-ids)
        menu-items     (zipmap ids-list items-with-ids)]
    (assoc nav :menu-items menu-items)))

(def ^:private handled-keycodes
  (let [{:keys [previous ok up left down right play pause play-pause]}
        keycodes/k->event-code
        directional       (set (flatten (list up left down right)))
        confirm           (set ok)
        meta-set          (set (flatten (list previous ok)))
        playback          (set (flatten (list play pause play-pause)))
        handled           (union directional confirm meta-set playback)]
    {:keys        handled
     :directional directional
     :playback    playback
     :confirm     confirm
     :meta        meta-set}))

(defn- handle-key-ev
  "Samsung (basic and smart remote control) key codes:
  https://developer.samsung.com/smarttv/develop/guides/user-interaction/remote-control.html

  LG (magic and conventional remote control) key codes:
  https://webostv.developer.lge.com/develop/guides/magic-remote"
  [event]
  (let [keyCode  (-> event .-keyCode)
        handled? (contains? (:keys handled-keycodes) keyCode)
        keycode-type #(contains? (get handled-keycodes %) keyCode)]
    (when handled?
      (cond
        (keycode-type :meta)
        (rf/dispatch [::handle-meta {:keyCode keyCode}])

        (keycode-type :directional)
        (rf/dispatch [::handle-navigation {:keyCode keyCode}])

        (keycode-type :playback)
        (rf/dispatch [::common-events/handle-playback-key {:keyCode keyCode}])))
    true))

(defn- handle-navigation
  [{:keys [db]} [_event-name {:keys [keyCode]}]]
  (let [playing? (get-in db [common-events/common-path :player :playing?])
        event (when playing?
                (cond
                  (contains? (:directional handled-keycodes) keyCode) {:dispatch [::common-events/show-player-controls true]}
                  :else nil))]
    (merge {:db db} event)))

(defn- navigate-to-page [{:keys [db]}]
  (let [{:keys [active-page active-menu]} db
        event (get-in db [:data active-page active-menu :active-item :event])
        slug  (:slug event)]
    (case (:__typename event)
      "tv_app_go_to_paywall_page"
      {:db (assoc db :paywall event)
       :dispatch [::navigate ::router/upgrade]}

      "tv_app_go_to_player"
      {:db db
       :dispatch [::common-events/open-player
                  {:id (:media_id event)}
                  {:slug (:playlist_slug event)}]}

      "tv_app_go_to_playlist_detail"
      {:db db
       :dispatch [::navigate ::router/playlist-page {:path-params {:playlist-id slug}}]})))

(defn- open-paywall [{:keys [db]} [_ paywall-event]]
  {:db (assoc db :paywall paywall-event)
   :dispatch [::navigate ::router/upgrade]})

(defn- close-paywall [{:keys [db]}]
  {:db (dissoc db :paywall)
   :dispatch [::navigate-back]})

(defn- handle-meta
  [{:keys [db] :as _fx} [_event-name {:keys [keyCode]}]]
  (let [player-open?                     (get-in db [common-events/common-path :player :playing?])
        controls-open?                   (get-in db [common-events/common-path :player :show-controls?])
        audio-track-open?                (get-in db [common-events/common-path :player :controls :track-selector-open?])
        active-playlist                  (-> db ::nav :active-playlist)
        {:keys [active-page menu-open?]} (::nav db)
        {:keys [ok previous]}            keycodes/k->event-code]
    (cond
      (contains? (set previous) keyCode) {:db         (update db ::nav dissoc :active-focus)
                                          :dispatch-n (cond
                                                        audio-track-open?
                                                        [[::common-events/toggle-track-selector]]

                                                        controls-open?
                                                        [[::common-events/show-player-controls false]]

                                                        (and player-open? active-playlist)
                                                        [[::fetch-playlist active-playlist]
                                                         [::common-events/close-player]]

                                                        (contains? #{:page-list :search} active-page)
                                                        [[::set-menu (not menu-open?)]
                                                         [::toggle-menu (not menu-open?)]]

                                                        (= active-page "playlist")
                                                        [[::navigate-back]]

                                                        :else
                                                        [[::navigate-back]])}
      (contains? (set ok) keyCode)       (if (and player-open? (not controls-open?))
                                           (rf/dispatch [::common-events/play-pause])
                                           (rf/dispatch [::common-events/click]))
      :else                              nil)))

(defn splash-played
  [db]
  (assoc db :splash-ended? true))

(defn- network-online
  [db _]
  (assoc db :network-offline? false))

(defn- network-offline
  [db _]
  (assoc db :network-offline? true))

(reg-fx ::back-navigation back-navigation!)
(reg-fx ::navigate-to navigate-to!)

(reg-event-fx ::navigate navigate-to-ev)
(reg-event-fx ::navigate-to-page nav-interceptors navigate-to-page)
(reg-event-fx ::navigate-back nav-interceptors navigate-back-ev)
(reg-event-fx ::router/route-changed (apply merge [(inject-cofx ::auth-events/auth-token)] nav-interceptors) route-changed-ev)
(reg-event-fx ::load-menu nav-interceptors load-menu-ev)

(reg-event-fx ::open-paywall nav-interceptors open-paywall)
(reg-event-fx ::close-paywall nav-interceptors close-paywall)

(reg-event-fx ::fetch-page nav-interceptors fetch-page)
(reg-event-fx ::fetch-page-error fetch-page-error)
(reg-event-fx ::fetch-page-success nav-interceptors fetch-page-success)

(reg-event-fx ::fetch-search nav-interceptors fetch-search)
(reg-event-fx ::set-search-term nav-interceptors set-search-term)
(reg-event-fx ::fetch-search-error nav-interceptors fetch-search-error)
(reg-event-fx ::fetch-search-success nav-interceptors fetch-search-success)

(reg-event-fx ::fetch-menu nav-interceptors fetch-menu)
(reg-event-fx ::fetch-menu-error nav-interceptors fetch-menu-error)
(reg-event-fx ::fetch-menu-success nav-interceptors fetch-menu-success)

(reg-event-fx ::keep-watching nav-interceptors keep-watching)
(reg-event-fx ::keep-watching-error nav-interceptors keep-watching-error)
(reg-event-db ::keep-watching-success nav-interceptors keep-watching-success)

(reg-event-fx ::set-active-menu nav-interceptors set-active-menu-ev)
(reg-event-db ::set-active-page nav-interceptors set-active-page-ev)
(reg-event-db ::set-active-item nav-interceptors set-active-item-ev)
(reg-event-db ::set-banner-transition-in-progress nav-interceptors set-banner-transition-in-progress-ev)

(reg-event-fx ::toggle-menu nav-interceptors toggle-menu-ev)
(reg-event-db ::set-menu nav-interceptors set-menu-open-state-ev)
(reg-event-db ::set-menu-items nav-interceptors set-menu-items-ev)

(reg-event-fx ::set-active-playlist nav-interceptors set-active-playlist-ev)
(reg-event-fx ::fetch-playlist nav-interceptors fetch-playlist)
(reg-event-fx ::fetch-playlist-error fetch-playlist-error)
(reg-event-fx ::fetch-playlist-success nav-interceptors fetch-playlist-success)

(reg-event-fx ::handle-navigation handle-navigation)
(reg-event-fx ::handle-meta handle-meta)
(reg-event-db ::splash-playback nav-interceptors splash-played)

(reg-event-db ::network-online network-online)
(reg-event-db ::network-offline network-offline)

;; when user presses any key we want to handle it
(set! (.-onkeydown js/window) (debounce handle-key-ev 100))
